import { useState, useEffect, useCallback } from "react";
import EmployeeOnLeaveFile from "FileGenerator/AdminFileGenerator/EmployeeOnLeave";
import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import moment from "moment";

function EmployeeOnLeaveService(upcomingOnly = false) {
  // State declarations
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState("All Departments");
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);

  //  Define correct columns matching the API fields
  const columns = [
    {
      title: "Employee Name",
      key: "fullName",
      render: (_, record) => `${record.employee?.fullName || ""}`.trim() || "Unknown",
    },
    {
      title: "Department",
      key: "departmentName",
      render: (_, record) => `${record.departmentName || ""}`.trim() || "Unknown",
    },
    {
      title: "Leave Type",
      key: "leaveType",
      render: (_, record) => record.leavePolicy?.policyName || "-",
    },
    {
      title: "From Date",
      dataIndex: "fromDate",
      key: "fromDate",
      render: (date) => (date ? moment(date).format("MMM-DD-YYYY") : "-"),
    },
    {
      title: "To Date",
      dataIndex: "toDate",
      key: "toDate",
      render: (date) => (date ? moment(date).format("MMM-DD-YYYY") : "-"),
    },
    {
      title: "Total Days",
      dataIndex: "totalDays",
      key: "totalDays",
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const statusMap = {
          0: "Pending",
          1: "Approved",
          2: "Rejected",
        };
        return statusMap[status] || "Unknown";
      },
    },
  ];

  const { handleDownloadReport } = EmployeeOnLeaveFile(selectedDepartment, rows);

  // 🔹 Fetch departments list - IMPROVED
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axiosInstance.get(API.department.getList);
        const dataArray = Array.isArray(response.data) ? response.data : response.data.data;
        setDepartments(response.data);
        if (!Array.isArray(dataArray)) {
          setDepartments([]);
          return;
        }
      } catch (error) {
        console.error("❌ Error fetching departments:", error);
      }
    };

    fetchDepartments();
  }, []);

  // 🔹 Fetch leave requests
  useEffect(() => {
    const fetchLeaveRequests = async () => {
      setLoading(true);
      try {
        const url = API.leaveRequest.getAll;

        const response = await axiosInstance.get(url, {
          params: { upcomingOnly: upcomingOnly.toString() },
        });

        // Handle different response structures
        let apiRows = [];

        if (Array.isArray(response.data)) {
          apiRows = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          apiRows = response.data.data;
        } else if (response.data && typeof response.data === "object") {
          const arrayKeys = Object.keys(response.data).filter((key) =>
            Array.isArray(response.data[key])
          );
          if (arrayKeys.length > 0) {
            apiRows = response.data[arrayKeys[0]];
          } else {
            apiRows = [response.data];
          }
        }

        setRows(apiRows);

        // Apply current department filter to the new data
        if (selectedDepartment === "All Departments") {
          setFilteredRows(apiRows);
        } else {
          const filtered = apiRows.filter((row) => row.departmentName === selectedDepartment);
          setFilteredRows(filtered);
        }
      } catch (error) {
        console.error("❌ Error fetching leave requests:", error);
        console.error("❌ Error details:", error.response?.data || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveRequests();
  }, [upcomingOnly]);

  // 🔹 Handle department filtering - IMPROVED (like your LeaveRequest component)
  const handleDepartmentChange = useCallback(
    (value) => {
      setSelectedDepartment(value);

      if (value === "All Departments") {
        setFilteredRows(rows);
      } else {
        const filtered = rows.filter((row) => {
          const matches = row.departmentName === value;
          return matches;
        });
        setFilteredRows(filtered);
      }
    },
    [rows]
  );

  return {
    columns,
    filteredRows: loading ? [] : filteredRows,
    handleDownloadReport,
    handleDepartmentChange,
    departments,
    selectedDepartment,
    loading,
    // Additional useful data
    totalRows: rows.length,
    filteredCount: filteredRows.length,
  };
}

export default EmployeeOnLeaveService;
